import { NebulaWebGameSDK, SDKEnv, SDKStatusCode } from "@nebula-sdk/compose";
import { getUnixTime } from "date-fns";
import bridge from "./bridge";
import { NebulaSiteCore } from "./core";
import message from "./message";
import type { NebulaSiteBridge } from "./bridge";
import type {
  SDKBridgeSHandler, SDKInitArgs, SDKInitResult, SDKLoginResult, SDKPlatform, SDKRequestOptions, SDKResponseOptions,
  SDKResult, SDKUserInfo,
} from "@nebula-sdk/compose";
import type { App } from "vue";

export class NebulaSiteAccountService {
  private readonly sdk: NebulaWebGameSDK = new NebulaWebGameSDK();

  public readonly core: NebulaSiteCore = new NebulaSiteCore();

  public readonly bridge: NebulaSiteBridge = bridge;

  public get token(): string | null {
    return this.core.token;
  }

  public get platform(): SDKPlatform {
    return this.core.platform;
  }

  public get userInfo(): SDKUserInfo | null {
    return this.core.userInfo;
  }

  private success(args: { message?: boolean; result: SDKResult<unknown> }) {
    if (args.message !== true || !args.result.msg) return;
    message.info({ content: args.result.msg ?? "成功" });
  }

  private error(args: { message?: boolean; result: SDKResult<unknown> }) {
    if (args.message === false || !args.result.msg) return;
    message.info({ content: args.result.msg ?? "错误" });
  }

  private async signature(args?: Record<string, unknown>) {
    if (this.bridge.support) {
      const source = Object.assign(args ?? {}, {
        company_id: this.bridge.env.companyId,
        app_id: this.bridge.env.appId,
        channel_id: this.bridge.env.channelId,
        device_id: this.bridge.env.deviceId,
        os: this.bridge.env.os,
        mac: this.bridge.env.mac,
        idfa: this.bridge.env.idfa,
        aaid: this.bridge.env.aaid,
        imei: this.bridge.env.imei,
        network_mode: this.bridge.env.networkMode,
        device_type: this.bridge.env.deviceType,
        os_version: this.bridge.env.osVersion,
        sdk_version: this.bridge.env.sdkVersion,
        timestamp: getUnixTime(Date.now()),
        source: this.bridge.env.source,
        union_id: this.bridge.env.unionId,
        api_version: this.bridge.env.apiVersion,
        token: this.bridge.env.token,
        theme_color: this.bridge.env.themeColor,
        sign_type: "md5",
        sign_nonce: this.core.generateRandomValues().join(""),
        sign_version: "1.0",
      });

      const keys = Object.keys(source).sort();
      const data: Record<string, unknown> = {};
      for (const key of keys) {
        if (source[key] === null) continue;
        if (source[key] === undefined) continue;
        data[key] = String(source[key] as any);
      }

      const result = await this.bridge.invoke(
        "FYPlatformJSInterface.asynHandler",
        {
          action: "getSign",
          data: { originalString: JSON.stringify(data) },
        },
        { json: true },
      );
      source.sign = result.sign;
      return source;
    }
    return this.core.signature(args ?? {}, this.core.options?.config.nbAppKey ?? "");
  }

  public async encrypt(plaintext: string) {
    if (this.bridge.support) {
      const result = await this.bridge.invoke(
        "FYPlatformJSInterface.asynHandler",
        {
          action: "getEncrypt",
          data: { secureString: plaintext },
        },
        { json: true },
      );
      return result.encrypt;
    }
    return this.core.encrypt({ key: this.core.options?.config.nbAppKey ?? "", plaintext });
  }

  public async decrypt(ciphertext: string) {
    if (this.bridge.support) {
      const result = await this.bridge.invoke(
        "FYPlatformJSInterface.asynHandler",
        {
          action: "getDecrypt",
          data: { originalString: ciphertext },
        },
        { json: true },
      );
      return result.decrypt;
    }
    return this.core.decrypt({ key: this.core.options?.config.nbAppKey ?? "", ciphertext });
  }

  // 日志上报
  public async scopeRequestTrack(args: {
    eventGroup?: string;
    eventLevel?: "info" | "warn" | "error";
    eventId: string;
    eventName: string;
    eventValue: Record<string, unknown>;
  }): Promise<SDKResult<null>> {
    const navigator = window.navigator as Navigator & {
      connection?: { effectiveType?: string };
      userAgentData?: { platform?: string };
    };

    return await this.core.track({
      appId: this.core.options?.config.nbAppId ?? "",
      appVersion: this.core.version,
      appChannel: SDKEnv.Web,
      userId: this.core.userInfo?.nbOpenId ?? "",
      userTag: this.core.userInfo?.openId ?? "",
      deviceId: this.core.deviceId,
      deviceModel: navigator.userAgentData?.platform ?? "unknown",
      osType: this.core.platform,
      osVersion: navigator.userAgent,
      networkType: navigator.connection?.effectiveType ?? "unknown",
      eventId: args.eventId,
      eventName: args.eventName,
      eventGroup: args.eventGroup ?? "",
      eventValue: args.eventValue,
      eventTime: this.core.formatDateTime(Date.now()),
      eventLevel: args.eventLevel ?? "info",
    });
  }

  // 通行证请求
  public async scopeRequestPassport<T = any>(
    args: SDKRequestOptions & { message?: boolean },
  ): Promise<SDKResponseOptions<T>> {
    const data = { ...args.data };

    if (!this.bridge.support) {
      data.app_id = data.app_id ?? this.core.options?.config.nbAppId;
      data.channel_id = data.channel_id ?? this.core.options?.config.nbChannelId;
      data.token = data.token ?? this.core.userInfo?.token;

      data.token = `eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJqdGkiOiI2YWRhYzA4ZmYxNDY5IiwiaWF0IjoxNzkyNzIxMDM5LCJuYmYiOjE3OTI3MjEwMzksImV4cCI6MTgwMDQ5NzAzOSwiYXBwX2lkIjoiMzAwMzUiLCJ1bmlvbl9pZCI6MjU2ODM1MX0.c74xVVJ2_TQrQWC51iSbdBQXDPsQo0C0S1qAYnJHEc4`;
    }

    const result = await this.core.requestPassport<T>({
      ...args,
      data: await this.signature(data),
    });

    if (result.status === "success") {
      this.success({ message: args.message, result });
    } else {
      this.error({ message: args.message, result });
    }

    void this.scopeRequestTrack({
      eventId: args.url,
      eventName: "通行证请求",
      eventGroup: "site-account-passport",
      eventValue: { args, result },
      eventLevel: result.status === "error" ? "error" : "info",
    });

    return result;
  }

  // 星云请求
  public async scopeRequestNebula<T = any>(
    args: SDKRequestOptions & { message?: boolean },
  ): Promise<SDKResult<T>> {
    const result = await this.core.request<T>({
      ...args,
      url: `${import.meta.env.VITE_NEBULA_API}${args.url}`,
      data: await this.signature(args.data),
    });

    if (result.status === "success") {
      this.success({ message: args.message, result });
    } else {
      this.error({ message: args.message, result });
    }

    void this.scopeRequestTrack({
      eventId: args.url,
      eventName: "星云请求",
      eventGroup: "site-account-nebula",
      eventValue: { args, result },
      eventLevel: result.status === "error" ? "error" : "info",
    });

    return result;
  }

  // 初始化
  public async init(args: SDKInitArgs): Promise<SDKInitResult> {
    if (this.bridge.support) {
      const result = await this.bridge.init();
      if (result.status === "success") {
        this.core.userInfo = {
          openId: this.bridge.env.openId,
          unionId: this.bridge.env.unionId,
          token: this.bridge.env.token,
          scene: "",
          nbOpenId: this.bridge.env.openId,
          nbUnionId: this.bridge.env.unionId,
          firstLogin: false,
          firstRegisterTime: 0,
        };
      }
      return await this.core.init({
        version: import.meta.env.VITE_SDK_VERSION,
        config: result.data!,
      });
    }
    const result = await this.core.init(args);
    if (result.status === "error") return result;
    return await this.sdk.init(this.core.options!);
  }

  // 登录
  public async login(): Promise<SDKLoginResult> {
    return await this.sdk.login();
  }

  // 退出
  public async logout(): Promise<SDKResult<null>> {
    this.core.userInfo = null;
    return await Promise.resolve({
      status: "success",
      code: SDKStatusCode.Success,
      msg: "退出登录成功",
      data: null,
    });
  }

  public async switch(): Promise<SDKResult<null>> {
    if (!this.bridge.support) {
      message.info({ content: "当前环境不支持切换账号" });
      return {
        status: "error",
        code: SDKStatusCode.Error,
        msg: "当前环境不支持切换账号",
        data: null,
      };
    }

    await this.bridge.invoke(
      "FYPlatformJSInterface.asynHandler",
      { action: "switchAccount", data: {} },
      { json: false },
    );

    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "切换账号成功",
      data: null,
    };
  }

  public on(handler: string, callback: SDKBridgeSHandler, options: { async: boolean }): void {
    if (!this.bridge.support) return;

    this.bridge.subscribe(handler, callback, options);
  }

  public off(handler: string, callback: SDKBridgeSHandler, options: { async: boolean }): void {
    if (!this.bridge.support) return;

    this.bridge.unsubscribe(handler, callback, options);
  }

  public install(app: App): void {
    Object.defineProperty(app.config.globalProperties, "$account", {
      enumerable: true,
      get: () => this,
    });
  }
}

export default new NebulaSiteAccountService();
